import { useEffect } from 'react'
import { useIsPresent } from 'framer-motion'
import { useActiveChatId, useSetActiveChat } from '@renderer/chat'
import { removeCompletedTurn } from '@renderer/chat/store/turn-status'
import { toastManager } from '@renderer/overlay/Toaster/toast-store'

/**
 * Claim `chatId` as the globally-active chat for a mounted transcript surface
 * (the Chat View, the Action View's chat drawer).
 *
 * Two policies every such surface owes, kept in one place:
 *
 * - Claiming also clears the active-chat footer cell entry and any completion
 *   toast for the chat — the user is looking at the thread already (HIG-4985).
 * - The claim is re-asserted when the idle timeout clears activeChatId while
 *   the surface is still open — otherwise reopening the panel renders a blank
 *   transcript (HIG-5479). Guarded on activeChatId being unset (not
 *   inequality) so it doesn't fight the temp→real id swap, and on presence so
 *   an exiting surface doesn't re-assert its chat the moment an incoming
 *   new-chat view clears activeChatId.
 *
 * `isNewChat` suppresses the re-assert for the Chat View's new-chat mode,
 * where activeChatId is deliberately cleared until a submit mints one.
 */
export function useClaimConversation(chatId: string | undefined, isNewChat = false): void {
  const setActiveChat = useSetActiveChat()
  const activeChatId = useActiveChatId()
  // False while this instance is the *outgoing* view of a swap — AnimatePresence
  // keeps it mounted through the exit animation, and its effects still run.
  const isPresent = useIsPresent()

  useEffect(() => {
    if (!chatId) return
    setActiveChat(chatId)
    removeCompletedTurn(chatId)
    toastManager.dismiss(`chat-complete-${chatId}`)
  }, [chatId, setActiveChat])

  useEffect(() => {
    if (!isPresent || !chatId || isNewChat || activeChatId) return
    setActiveChat(chatId)
  }, [isPresent, chatId, isNewChat, activeChatId, setActiveChat])
}
